import { gql } from "generated-graphql";
import {
  ProductPickerFragment,
  ProductsQueryQuery,
} from "generated-graphql/graphql";
import { forwardRef, useCallback, useState } from "react";
import { ControllerFieldState } from "react-hook-form";
import AsyncAutocomplete from "./Forms/AsyncAutocomplete";
import { ErrorDisplay } from "./Forms/ErrorDisplay";
import { FormTextField } from "./Forms/FormTextField";

gql(/* GraphQL */ `
  fragment ProductPicker on Product {
    id
    name
    alternateId
  }
`);

const PRODUCTS_QUERY = gql(/* GraphQL */ `
  query ProductsQuery(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $facilityId: ID
    $reportingYear: Int
  ) {
    products(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...ProductPicker
        facilityProducts(
          facilityId: $facilityId
          reportingYear: $reportingYear
        ) {
          id
        }
      }
      count
    }
  }
`);

type Product = ProductsQueryQuery["products"]["items"][number];

type ProductPickerProps = ControllerFieldState & {
  onChange: (product: Product | null) => void;
  defaultSearchTerm?: string;
  value?: ProductPickerFragment | null;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  isOptionDisabled?: (product: Product) => boolean;
  facilityId?: string;
  reportingYear?: number;
};

export const ProductPicker = forwardRef<HTMLDivElement, ProductPickerProps>(
  function ProductPicker(
    {
      onChange,
      defaultSearchTerm = "",
      value: initialValue,
      error,
      required,
      disabled,
      readOnly,
      isOptionDisabled,
      facilityId,
      reportingYear,
    },
    ref
  ) {
    const [value, setValue] = useState(initialValue ?? null);

    const componentLabel = "Product Name";

    const getProductDisplayText = useCallback(
      (option: Product) =>
        option.alternateId?.trim()?.length
          ? `${option.name} (${option.alternateId})`
          : option.name,
      []
    );

    if (readOnly) {
      return (
        <>
          <FormTextField
            label={componentLabel}
            readOnly
            defaultValue={value ? getProductDisplayText(value) : ""}
          />
        </>
      );
    }

    return (
      <>
        <AsyncAutocomplete
          ref={ref}
          query={PRODUCTS_QUERY}
          variables={{ facilityId, reportingYear }}
          initialPageSize={25}
          additionalSearchString={defaultSearchTerm}
          getItems={(data: ProductsQueryQuery) => data.products.items}
          getOptionLabel={(option: Product) => getProductDisplayText(option)}
          placeholderLabel={componentLabel}
          onChange={(val: Product | null) => {
            setValue(val);
            onChange(val);
          }}
          value={value}
          error={error}
          disabled={disabled}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionDisabled={isOptionDisabled}
          lazyLoad
          noOptionsText="Search for products in your inventory"
          textFieldProps={{
            required,
            autoComplete: "off",
            inputProps: {
              autoComplete: "off",
            },
          }}
        />
      </>
    );
  }
);
